import { useBranding } from "@app-setup:BrandingContext";
import { Sidebar } from "@components:Sidebar/Sidebar";
import { useRBACCheck } from "@hooks:useRBAC";
import { IconButton } from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { generateSidebarContrastColors } from "@utils:color";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FaUsers } from "react-icons/fa6";
import {
  MdAccountCircle,
  MdColorLens,
  MdDomain,
  MdKeyboardArrowLeft,
  MdTune,
} from "react-icons/md";
import { RiRobot2Fill } from "react-icons/ri";
import { useLocation, useNavigate } from "react-router";

import { DEFAULT_ROUTE_AFTER_LOGIN, Routes } from "@:constants";
import { RBACAction, RBACResource } from "@:rbac";

export const SettingsSidebar = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { config } = useBranding();
  const { t } = useTranslation();

  // Calculate header text color based on sidebar background
  const headerTextColor = useMemo(() => {
    if (config?.sidebarColor) {
      return generateSidebarContrastColors(config.sidebarColor).textColor;
    }
    return "#000000"; // Default dark text
  }, [config?.sidebarColor]);

  const canAccessProfile = useRBACCheck(
    RBACAction.read,
    RBACResource["settings__profile"],
  );
  const canAccessOrganizations = useRBACCheck(
    RBACAction.read,
    RBACResource["settings__organizations"],
  );
  const canAccessUsers = useRBACCheck(
    RBACAction.read,
    RBACResource["settings__users"],
  );
  const canAccessBots = useRBACCheck(
    RBACAction.read,
    RBACResource["settings__bots"],
  );
  const canAccessUiPreferences = useRBACCheck(
    RBACAction.read,
    RBACResource["settings__ui_preferences"],
  );
  const canAccessUiBranding = useRBACCheck(
    RBACAction.read,
    RBACResource["settings__ui_branding"],
  );

  return (
    <Sidebar
      sx={{
        mx: "10px",
      }}
      backgroundColor={config?.sidebarColor}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
        className="SettingsSidebar__Header"
      >
        <IconButton
          sx={{
            position: "absolute",
            left: "0",
            color: headerTextColor,
          }}
          onClick={() => {
            navigate(DEFAULT_ROUTE_AFTER_LOGIN);
          }}
        >
          <MdKeyboardArrowLeft />
        </IconButton>

        <Typography fontSize={"30px"} sx={{ color: headerTextColor }}>
          {t("settings.title")}
        </Typography>
      </Box>

      <Sidebar.Menu
        subHeading={t("settings.sections.myAccount")}
        sx={{
          marginTop: "50px",
        }}
        hidden={!canAccessProfile}
      >
        <Sidebar.MenuItem
          icon={<MdAccountCircle />}
          onClick={() => {
            navigate(Routes.SettingsProfile);
          }}
          selected={pathname === Routes.SettingsProfile}
          hidden={!canAccessProfile}
        >
          {t("settings.nav.profile")}
        </Sidebar.MenuItem>
      </Sidebar.Menu>

      <Sidebar.Menu
        subHeading={t("settings.sections.tenant")}
        sx={{
          marginTop: "20px",
        }}
        hidden={!canAccessOrganizations && !canAccessUsers && !canAccessBots}
      >
        <Sidebar.MenuItem
          icon={<MdDomain />}
          onClick={() => {
            navigate(Routes.SettingsOrganizations);
          }}
          selected={pathname === Routes.SettingsOrganizations}
          hidden={!canAccessOrganizations}
        >
          {t("settings.nav.organizations")}
        </Sidebar.MenuItem>

        <Sidebar.MenuItem
          icon={<FaUsers />}
          onClick={() => {
            navigate(Routes.SettingsUsers);
          }}
          selected={pathname === Routes.SettingsUsers}
          hidden={!canAccessUsers}
        >
          {t("settings.nav.users")}
        </Sidebar.MenuItem>

        <Sidebar.MenuItem
          icon={<RiRobot2Fill />}
          onClick={() => {
            navigate(Routes.SettingsBots);
          }}
          selected={pathname === Routes.SettingsBots}
          hidden={!canAccessBots}
        >
          {t("settings.nav.bots")}
        </Sidebar.MenuItem>
      </Sidebar.Menu>
      <Sidebar.Menu
        subHeading={t("settings.sections.preferences")}
        sx={{
          marginTop: "20px",
        }}
        hidden={!canAccessUiPreferences && !canAccessUiBranding}
      >
        <Sidebar.MenuItem
          icon={<MdTune />}
          onClick={() => {
            navigate(Routes.SettingsUiPreferences);
          }}
          selected={pathname === Routes.SettingsUiPreferences}
          hidden={!canAccessUiPreferences}
        >
          {t("settings.nav.uiPreferences")}
        </Sidebar.MenuItem>
        <Sidebar.MenuItem
          icon={<MdColorLens />}
          onClick={() => {
            navigate(Routes.SettingsUiBranding);
          }}
          selected={pathname === Routes.SettingsUiBranding}
          hidden={!canAccessUiBranding}
        >
          {t("settings.nav.uiBranding")}
        </Sidebar.MenuItem>
      </Sidebar.Menu>
    </Sidebar>
  );
};
